import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, Smartphone } from "lucide-react";
import { PageShell } from "../components/PageShell";
import { PROFILE_META, PROFILES, LEVELS, normalizeProfile, normalizeLevel } from "../config/program";
import { getExtDay } from "../config/program20";

const PROFILE_SHORT = {
  R: "Ritenzione e microcircolo",
  D: "Addominale e digestivo",
  P: "Sedentarietà e postura",
};

const storageKey = (day) => `lf_checkin_${day}`;
const key2 = (day) => `lf_checkin2_${day}`;
const readCheckin = (day) => {
  try {
    return JSON.parse(localStorage.getItem(storageKey(day)));
  } catch {
    return null;
  }
};
const read2 = (day) => {
  try {
    return JSON.parse(localStorage.getItem(key2(day)));
  } catch {
    return null;
  }
};

export default function CheckinPage() {
  const { day } = useParams();
  const dayNum = parseInt(day, 10);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const qp = normalizeProfile(searchParams.get("p"));
  const ql = normalizeLevel(searchParams.get("l"));
  const [pickP, setPickP] = useState(null);
  const [pickL, setPickL] = useState(null);
  const profile = qp || pickP;
  const level = ql || pickL;

  const [bloating, setBloating] = useState(5);
  const [second, setSecond] = useState("");
  const [second2, setSecond2] = useState(5);
  const [saved, setSaved] = useState(false);
  const [cleared, setCleared] = useState(false);
  const isExt = dayNum >= 5;

  const clearCheckins = () => {
    Object.keys(localStorage)
      .filter((k) => k.startsWith("lf_checkin_") || k.startsWith("lf_checkin2_") || k === "lf_choice")
      .forEach((k) => localStorage.removeItem(k));
    setBloating(5);
    setSecond("");
    setSecond2(5);
    setSaved(false);
    setCleared(true);
  };

  useEffect(() => {
    if (!(dayNum >= 1 && dayNum <= 20)) {
      navigate("/");
      return;
    }
    const existing = readCheckin(dayNum);
    if (existing) {
      setBloating(existing.bloating);
      setSecond(existing.second || "");
    }
    const e2 = read2(dayNum);
    if (e2) setSecond2(e2.value);
  }, [dayNum, navigate]);

  const validDay = dayNum >= 1 && dayNum <= 20;
  const query = profile && level ? `?p=${profile}&l=${level}` : "";

  if (!validDay) return null;

  if (!profile || !level) {
    return (
      <PageShell back onBack={() => navigate("/")}>
        <div className="flex flex-1 flex-col pt-2" data-testid="checkin-quick-picker">
          <h2 className="font-serif text-2xl font-bold leading-tight text-ink">Check-in · Giorno {dayNum}</h2>
          <p className="mt-3 text-sm leading-relaxed text-ink/70">
            Scegli il tuo profilo e il tuo livello. Questa scelta non viene salvata.
          </p>
          <p className="mt-6 text-xs font-semibold uppercase tracking-wider text-coral">Profilo</p>
          <div className="mt-2 flex flex-col gap-2">
            {PROFILES.map((p) => (
              <button key={p} type="button" onClick={() => setPickP(p)} data-testid={`pick-profile-${p}`}
                className={`rounded-2xl border-2 px-4 py-3 text-left text-sm font-medium transition-colors ${
                  pickP === p ? "border-coral bg-coral-soft text-ink" : "border-transparent bg-white/80 text-ink/80"
                }`}>
                {PROFILE_SHORT[p]}
              </button>
            ))}
          </div>
          <p className="mt-6 text-xs font-semibold uppercase tracking-wider text-coral">Livello</p>
          <div className="mt-2 flex gap-2">
            {LEVELS.map((l) => (
              <button key={l} type="button" onClick={() => setPickL(l)} data-testid={`pick-level-${l}`}
                className={`flex-1 rounded-2xl border-2 px-4 py-3 text-sm font-medium transition-colors ${
                  pickL === l ? "border-coral bg-coral text-white" : "border-ink/15 bg-white text-ink/70"
                }`}>
                {l}
              </button>
            ))}
          </div>
        </div>
      </PageShell>
    );
  }

  const meta = PROFILE_META[profile];
  const extDay = isExt ? getExtDay(profile, dayNum) : null;

  const save = () => {
    if (!isExt && !second) return;
    localStorage.setItem(
      storageKey(dayNum),
      JSON.stringify({ bloating, second: isExt ? null : second, profile, level, savedAt: new Date().toISOString() })
    );
    if (isExt) {
      localStorage.setItem(key2(dayNum), JSON.stringify({ value: second2, question: extDay.question }));
    }
    setSaved(true);
  };

  const cmpDay1 = readCheckin(1);
  const cmpDay4 = readCheckin(4);

  return (
    <PageShell back onBack={() => navigate(`/giorno/${dayNum}${query}`)}>
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="flex flex-1 flex-col pt-1"
        data-testid={`checkin-page-${dayNum}`}
      >
        <p className="text-xs font-semibold uppercase tracking-wider text-coral">Giorno {dayNum}</p>
        <h2 className="mt-1 font-serif text-3xl font-bold leading-tight text-ink">Check-in</h2>
        <span className="mt-3 inline-flex w-fit items-center rounded-full bg-coral/15 px-4 py-1.5 text-xs font-medium text-coral">
          {meta.name} · {level}
        </span>

        {!saved ? (
          <div className="mt-7 rounded-2xl bg-white/80 p-5 shadow-sm">
            <p className="text-sm font-medium text-ink/80">Quanto ti senti gonfia oggi?</p>
            <div className="mt-3 flex items-center gap-3">
              <input
                type="range" min="1" max="10" value={bloating}
                onChange={(e) => setBloating(parseInt(e.target.value, 10))}
                data-testid="checkin-bloating-slider"
                className="h-2 w-full cursor-pointer appearance-none rounded-full bg-ink/10"
                style={{ accentColor: "#F97054" }}
              />
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-coral text-sm font-bold text-white" data-testid="checkin-bloating-value">
                {bloating}
              </span>
            </div>

            <p className="mt-5 text-sm font-medium text-ink/80">
              {isExt ? extDay.question : meta.checkin2.question}
            </p>
            {isExt ? (
              <div data-testid="checkin-second-slider-wrap">
                <div className="mt-3 flex items-center gap-3">
                  <input
                    type="range" min="1" max="10" value={second2}
                    onChange={(e) => setSecond2(parseInt(e.target.value, 10))}
                    data-testid="checkin-second-slider"
                    className="h-2 w-full cursor-pointer appearance-none rounded-full bg-ink/10"
                    style={{ accentColor: "#F97054" }}
                  />
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-coral text-sm font-bold text-white" data-testid="checkin-second-value">
                    {second2}
                  </span>
                </div>
                <div className="mt-1 flex justify-between text-xs text-ink/45">
                  <span>per niente</span>
                  <span>moltissimo</span>
                </div>
              </div>
            ) : (
              <div className="mt-3 flex flex-wrap gap-2">
                {meta.checkin2.options.map((opt) => (
                  <button key={opt} type="button" onClick={() => setSecond(opt)}
                    data-testid={`checkin-second-${opt}`}
                    className={`rounded-full border-2 px-4 py-2 text-sm font-medium capitalize transition-colors ${
                      second === opt ? "border-coral bg-coral text-white" : "border-ink/15 bg-white text-ink/70"
                    }`}>
                    {opt}
                  </button>
                ))}
              </div>
            )}

            <button
              type="button" onClick={save} disabled={!isExt && !second}
              data-testid="checkin-submit-button"
              className="mt-6 w-full rounded-2xl bg-coral px-6 py-3.5 text-base font-semibold text-white shadow-md shadow-coral/30 transition-all hover:-translate-y-0.5 disabled:opacity-50"
            >
              Salva il check-in
            </button>
            <p className="mt-3 text-center text-xs text-ink/55" data-testid="checkin-device-note">
              Le tue risposte restano solo su questo dispositivo.
            </p>
          </div>
        ) : (
          <SavedBlock
            dayNum={dayNum}
            bloating={bloating}
            cmpDay1={cmpDay1}
            cmpDay4={cmpDay4}
            onNext={() => navigate(`/giorno/${dayNum + 1}${query}`)}
          />
        )}

        <div className="mt-8 text-center">
          <button
            type="button"
            onClick={clearCheckins}
            data-testid="clear-checkins-button"
            className="text-xs font-medium text-ink/50 underline underline-offset-4 transition-colors hover:text-coral"
          >
            Cancella i miei check-in da questo dispositivo
          </button>
          {cleared && (
            <p className="mt-2 text-xs text-coral" data-testid="clear-checkins-done">
              Check-in cancellati da questo dispositivo.
            </p>
          )}
        </div>
      </motion.div>
    </PageShell>
  );
}

const SavedBlock = ({ dayNum, bloating, cmpDay1, cmpDay4 }) => (
  <motion.div
    initial={{ opacity: 0, y: 14 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.35 }}
    className="mt-7"
    data-testid="checkin-saved"
  >
    <div className="flex items-center gap-2 rounded-2xl bg-coral-soft p-4" data-testid="checkin-saved-message">
      <Check size={20} className="shrink-0 text-coral" />
      <p className="text-sm font-medium text-ink">Check-in salvato. Un piccolo passo in più, con calma.</p>
    </div>

    {dayNum === 4 && <Day4Compare bloating={bloating} day1={cmpDay1} />}
    {dayNum === 14 && <TwoCompare labelB="Giorno 14" bloating={bloating} day1={cmpDay1} testid="checkin-day14" />}
    {dayNum === 20 && <Day20Compare bloating={bloating} cmpDay1={cmpDay1} cmpDay4={cmpDay4} />}

    {dayNum < 20 ? (
      <p
        className="mt-6 text-center text-sm font-medium text-ink/70"
        data-testid="checkin-come-back"
      >
        Torna domani per il giorno successivo.
      </p>
    ) : null}

    <p className="mt-4 text-center text-xs text-ink/55">
      Le tue risposte restano solo su questo dispositivo.
    </p>
  </motion.div>
);

const TwoCompare = ({ labelB, bloating, day1, testid }) => {
  if (!day1) {
    return (
      <div className="mt-5 rounded-2xl bg-white/80 p-5 shadow-sm" data-testid={`${testid}-nodata`}>
        <div className="flex items-center justify-center rounded-xl bg-coral/5 py-4">
          <Stat label="Oggi" value={bloating} />
        </div>
        <div className="mt-4 flex gap-3">
          <Smartphone size={18} className="mt-0.5 shrink-0 text-coral" />
          <p className="text-sm leading-relaxed text-ink/75">
            Per vedere il confronto, usa lo stesso dispositivo del giorno 1.
          </p>
        </div>
      </div>
    );
  }
  const improved = bloating < day1.bloating;
  const message = improved
    ? "Bene! Rispetto al giorno 1 senti il gonfiore un po' meno. Continua con calma e ascolta il tuo corpo."
    : "Il corpo ha tempi diversi. Non scoraggiarti: ogni percorso è a sé e i cambiamenti arrivano con costanza.";
  return (
    <div className="mt-5 rounded-2xl bg-white/80 p-5 shadow-sm" data-testid={`${testid}-compare`}>
      <h3 className="font-serif text-xl font-semibold text-ink">Giorno 1 e {labelB}</h3>
      <div className="mt-4 flex items-center justify-around rounded-xl bg-coral/5 py-4">
        <Stat label="Giorno 1" value={day1.bloating} />
        <ArrowRight size={20} className="text-ink/30" />
        <Stat label={labelB} value={bloating} />
      </div>
      <p className="mt-4 text-sm leading-relaxed text-ink/80" data-testid={`${testid}-message`}>{message}</p>
    </div>
  );
};

const Day20Compare = ({ bloating, cmpDay1, cmpDay4 }) => (
  <div className="mt-5 rounded-2xl bg-white/80 p-5 shadow-sm" data-testid="checkin-day20-compare">
    <h3 className="font-serif text-xl font-semibold text-ink">Il tuo percorso</h3>
    <div className="mt-4 flex items-center justify-around rounded-xl bg-coral/5 py-4">
      {cmpDay1 && <Stat label="Giorno 1" value={cmpDay1.bloating} />}
      {cmpDay1 && <ArrowRight size={18} className="text-ink/30" />}
      {cmpDay4 && <Stat label="Giorno 4" value={cmpDay4.bloating} />}
      {cmpDay4 && <ArrowRight size={18} className="text-ink/30" />}
      <Stat label="Giorno 20" value={bloating} />
    </div>
    <p className="mt-4 text-sm leading-relaxed text-ink/80" data-testid="checkin-day20-message">
      Hai completato il percorso. Il tuo corpo ti ha dato informazioni utili: tieni la tua sequenza.
    </p>
  </div>
);

const Day4Compare = ({ bloating, day1 }) => {
  if (!day1) {
    return (
      <div className="mt-5 rounded-2xl bg-white/80 p-5 shadow-sm" data-testid="checkin-day4-nodata">
        <div className="flex items-center justify-center rounded-xl bg-coral/5 py-4">
          <Stat label="Oggi" value={bloating} />
        </div>
        <div className="mt-4 flex gap-3">
          <Smartphone size={18} className="mt-0.5 shrink-0 text-coral" />
          <p className="text-sm leading-relaxed text-ink/75">
            Per vedere il confronto, usa lo stesso dispositivo del giorno 1.
          </p>
        </div>
      </div>
    );
  }

  const improved = bloating < day1.bloating;
  const message = improved
    ? "Bene! Rispetto al giorno 1 senti il gonfiore un po' meno. Continua con calma e ascolta il tuo corpo."
    : "Il corpo ha tempi diversi. Non scoraggiarti: ogni percorso è a sé e i cambiamenti arrivano con costanza.";

  return (
    <div className="mt-5 rounded-2xl bg-white/80 p-5 shadow-sm" data-testid="checkin-day4-compare">
      <h3 className="font-serif text-xl font-semibold text-ink">Giorno 1 e Giorno 4</h3>
      <div className="mt-4 flex items-center justify-around rounded-xl bg-coral/5 py-4">
        <Stat label="Giorno 1" value={day1.bloating} />
        <ArrowRight size={20} className="text-ink/30" />
        <Stat label="Giorno 4" value={bloating} />
      </div>
      <p className="mt-4 text-sm leading-relaxed text-ink/80" data-testid="checkin-day4-message">{message}</p>
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div className="flex flex-col items-center">
    <span className="text-xs text-ink/55">{label}</span>
    <span className="mt-1 flex h-11 w-11 items-center justify-center rounded-full bg-coral text-lg font-bold text-white">
      {value}
    </span>
  </div>
);
